import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import {
  isPendingAction,
  isRejectedAction,
  isFulfilledAction
} from '../actions';

const name = 'user';

export const login = createAsyncThunk(
  `${name}/login`,
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/auth/login/local', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const logout = createAsyncThunk(
  `${name}/logout`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/api/auth/logout');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getUser = createAsyncThunk(
  `${name}/getUser`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/auth/user`, {
        params: {
          t: new Date().getTime()
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue({ ...error.response.data, notify: false });
    }
  }
);

export const changePassword = createAsyncThunk(
  `${name}/changePassword`,
  async (data, { rejectWithValue }) => {
    try {
      await axios.post('/api/auth/change-password', data);
      return true;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const changeSessionOrganization = createAsyncThunk(
  `${name}/changeSessionOrganization`,
  async (organizationId) => {
    try {
      const response = await axios.get(
        `/api/auth/organizations/${organizationId}`
      );
      return response.data;
    } catch (error) {
      return false;
    }
  }
);

export const forgotPassword = createAsyncThunk(
  `${name}/forgotPassword`,
  async (data, {rejectWithValue}) => {
    try {
      const response = await axios.post(`/api/auth/forgot-password`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const resetPassword = createAsyncThunk(
  `${name}/resetPassword`,
  async (data, { rejectWithValue}) => {
    try {
      const { token, ...payload } = data;
      const response = await axios.post(`/api/auth/reset-password/${token}`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const validateToken = createAsyncThunk(
  `${name}/validateToken`,
  async ({ url, path , token }) => {
    try {
      const response = await axios.get(url || `/api/auth/${path}/${token}`);
      return response.data ?? true;
    } catch (error) {
      return false;
    }
  }
);

export const activate = createAsyncThunk(
  `${name}/activate`,
  async (data, { rejectWithValue }) => {
    try {
      const { token, ...payload } = data;
      const response = await axios.post(`/api/auth/activate/${token}`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const refreshUser = (state, action) => {
  const user = action?.payload;

  if (!user) {
    state.isLoggedIn = false;
    return;
  }

  state.isLoggedIn = true;
  state.user = user;
};

const resetUser = (state) => {
  window.localStorage.removeItem('organization');
  state.errors = false;
  state.user = null;
  state.isLoggedIn = false;
};

const authSlice = createSlice({
  name,
  initialState: {
    loading: true,
    errors: false,
    user: null,
    isLoggedIn: false
  },
  reducers: {
    updateUserOrganization: (state, action) => {
      const { payload } = action;
      let updatedOrganizations;

      if (state.user.organizations?.some((t) => t.id === payload.id)) {
        // update existing user.organization
        updatedOrganizations = (state.user.organizations || []).map((t) =>
          t.id === payload.id
            ? {
                ...t,
                ...payload
              }
            : t
        );
      } else {
        // add new user.organization
        updatedOrganizations = (state.user.organizations || []).concat({
          roles: [],
          ...payload
        });
      }

      state.user = {
        ...state.user,
        organizations: updatedOrganizations
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.fulfilled, (state, action) => {
        refreshUser(state, action);
      })
      .addCase(logout.fulfilled, (state, action) => {
        resetUser(state, action);
      })
      .addCase(getUser.fulfilled, (state, action) => {
        refreshUser(state, action);
      })
      .addCase(changeSessionOrganization.fulfilled, (state, action) => {
        refreshUser(state, action);
      })
      .addMatcher(isFulfilledAction(name), (state) => {
        state.loading = false;
      })
      .addMatcher(isPendingAction(name), (state) => {
        state.loading = true;
        state.errors = false;
      })
      .addMatcher(isRejectedAction(name), (state, action) => {
        if (action.type !== `${name}/changePassword/rejected`) {
          resetUser(state, action);
        }
        state.loading = false;
        state.errors = action.payload;
      });
  }
});

export const {
  selectRole,
  currentOrganization,
  organizationId,
  updateUserOrganization
} = authSlice.actions;
export default authSlice.reducer;
